import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import * as claimsApi from './api';
import * as coverageApi from './coverageApi';
import * as packageApi from './packageApi';
import * as dischargeApi from './dischargeApi';
import { getAllUsers } from '@/features/admin/api';
import { getAdmission } from '@/api';
import type { Admission } from '@/components/DischargeSummaryChat';
import type {
  CreateEstimateInput, CreateAuthorizationInput, SendAuthorizationInput, DecideAuthorizationInput,
  CreateQueryInput, ReplyQueryInput, CreateSubmissionInput,
} from './types';
import type { CoverageInput } from './coverageTypes';

/** Explicit claim creation (`POST /api/claims`) — only needed by coverage
 * capture's reimbursement path (see api.ts#createClaim's own comment).
 * Invalidates the worklist and the for-admission lookup, same as every
 * other claim-affecting mutation. */
export function useCreateClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: claimsApi.CreateClaimInput) => claimsApi.createClaim(input),
    onSuccess: (claim) => {
      queryClient.invalidateQueries({ queryKey: keys.claims.list() });
      if (claim.admission_id != null) {
        queryClient.invalidateQueries({ queryKey: keys.claims.forAdmission(claim.admission_id) });
      }
    },
  });
}

export function useClaims(params?: claimsApi.ClaimListParams) {
  return useQuery({
    queryKey: keys.claims.list(params as Record<string, unknown> | undefined),
    queryFn: () => claimsApi.fetchClaims(params),
  });
}

export function useClaim(id: number | null) {
  return useQuery({
    queryKey: keys.claims.detail(id ?? 0),
    queryFn: () => claimsApi.fetchClaim(id as number),
    enabled: id != null,
  });
}

/** The claim open against one admission, if any — used by IPModule.tsx's
 * enhancement chip. A cancelled/closed claim still matches (so a
 * self-pay-converted admission's history is visible) but is filtered out by
 * the caller when deciding whether to show a live enhancement alert. */
export function useClaimForAdmission(admissionId: number | null) {
  return useQuery({
    queryKey: keys.claims.forAdmission(admissionId ?? 0),
    queryFn: () => claimsApi.fetchClaims({ admission_id: admissionId as number }),
    enabled: admissionId != null,
    select: (rows) => rows[0] ?? null,
  });
}

function invalidateClaim(queryClient: ReturnType<typeof useQueryClient>, claimId: number) {
  queryClient.invalidateQueries({ queryKey: keys.claims.detail(claimId) });
  queryClient.invalidateQueries({ queryKey: keys.claims.forms(claimId) });
  queryClient.invalidateQueries({ queryKey: keys.claims.list() });
}

export function useCreateEstimate(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEstimateInput) => claimsApi.createEstimate(claimId, input),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useCreateAuthorization(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAuthorizationInput) => claimsApi.createAuthorization(claimId, input),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useSendAuthorization(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ authId, input }: { authId: number; input?: SendAuthorizationInput }) => claimsApi.sendAuthorization(claimId, authId, input),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useDecideAuthorization(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ authId, input }: { authId: number; input: DecideAuthorizationInput }) => claimsApi.decideAuthorization(claimId, authId, input),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useCreateQuery(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQueryInput) => claimsApi.createQuery(claimId, input),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useReplyQuery(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ queryId, input }: { queryId: number; input: ReplyQueryInput }) => claimsApi.replyQuery(claimId, queryId, input),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useCreateSubmission(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubmissionInput) => claimsApi.createSubmission(claimId, input),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useClaimInvoices(claimId: number | null) {
  return useQuery({
    queryKey: keys.claims.invoices(claimId ?? 0),
    queryFn: () => claimsApi.fetchClaimInvoices(claimId as number),
    enabled: claimId != null,
  });
}

export function usePostSettlement(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: import('./types').PostSettlementInput) => claimsApi.postSettlement(claimId, input),
    onSuccess: () => {
      invalidateClaim(queryClient, claimId);
      queryClient.invalidateQueries({ queryKey: keys.claims.invoices(claimId) });
    },
  });
}

// ── Documents, forms, packet (Phase 5 — wireframe S6) ──────────────────
// The checklist itself travels inside `useClaim()`'s own `document_checklist`
// — no separate query hook needed, since `GET /:id` already auto-resolves it
// (same call `GET /:id/documents` makes). These mutations just invalidate
// the claim so the next render sees the refreshed checklist.

export function useAttachClaimDocument(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ docKey, documentId }: { docKey: string; documentId: number }) => claimsApi.attachClaimDocument(claimId, docKey, documentId),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useWaiveClaimDocument(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ checklistId, waive, waivedReason }: { checklistId: number; waive: boolean; waivedReason?: string }) =>
      claimsApi.waiveClaimDocument(claimId, checklistId, waive, waivedReason),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useUpdateClaim(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Parameters<typeof claimsApi.updateClaim>[1]) => claimsApi.updateClaim(claimId, patch),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

/** Every user (same cached roster as useUsersMap) — for pickers. */
export function useUsersList() {
  return useQuery({ queryKey: keys.config.users(), queryFn: () => getAllUsers(), staleTime: 5 * 60 * 1000 });
}

export function useClaimFormsStatus(claimId: number | null) {
  return useQuery({
    queryKey: keys.claims.forms(claimId ?? 0),
    queryFn: () => claimsApi.fetchClaimFormsStatus(claimId as number),
    enabled: claimId != null,
  });
}

export function useGenerateClaimForm(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: 'preauth' | 'part_b') => claimsApi.generateClaimForm(claimId, form),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

export function useBuildClaimPacket(claimId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stage, format }: { stage: 'claim' | 'final'; format: 'pdf' | 'zip' }) => claimsApi.buildClaimPacket(claimId, stage, format),
    onSuccess: () => invalidateClaim(queryClient, claimId),
  });
}

// ── Coverage / payer capture (wireframe S1) ────────────────────────────

export function usePayerSchemes() {
  return useQuery({
    queryKey: keys.ip.payerSchemes(),
    queryFn: () => coverageApi.fetchPayerSchemes(),
    staleTime: Infinity, // a fixed category master, same reasoning as every other tenant-wide picklist
  });
}

export function useAdmissionPayers(admissionId: number | null) {
  return useQuery({
    queryKey: keys.ip.payers(admissionId ?? 0),
    queryFn: () => coverageApi.fetchAdmissionPayers(admissionId as number),
    enabled: admissionId != null,
  });
}

function invalidateCoverage(queryClient: ReturnType<typeof useQueryClient>, admissionId: number) {
  queryClient.invalidateQueries({ queryKey: keys.ip.payers(admissionId) });
  // A coverage save can auto-open a claim (ensureClaimForCoverage) — the
  // worklist/for-admission views must see it without a manual refresh.
  queryClient.invalidateQueries({ queryKey: keys.claims.list() });
  queryClient.invalidateQueries({ queryKey: keys.claims.forAdmission(admissionId) });
}

export function useCreateCoverage(admissionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CoverageInput) => coverageApi.createCoverage(admissionId, input),
    onSuccess: () => invalidateCoverage(queryClient, admissionId),
  });
}

export function useUpdateCoverage(admissionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ coverageId, input }: { coverageId: number; input: Partial<CoverageInput> }) =>
      coverageApi.updateCoverage(coverageId, input),
    onSuccess: () => invalidateCoverage(queryClient, admissionId),
  });
}

export function useVerifyCoverageId(admissionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (coverageId: number) => coverageApi.verifyCoverageId(coverageId),
    onSuccess: () => invalidateCoverage(queryClient, admissionId),
  });
}

// ── Package application (Insurance Claims Phase 6.1-6.3) ────────────────

export function useAdmissionPackages(admissionId: number | null) {
  return useQuery({
    queryKey: keys.ip.package(admissionId ?? 0),
    queryFn: () => packageApi.fetchAdmissionPackages(admissionId as number),
    enabled: admissionId != null,
  });
}

export function useApplyPackage(admissionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (packageId: number) => packageApi.applyPackage(admissionId, packageId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.ip.package(admissionId) }),
  });
}

export function useRemovePackage(admissionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (packageApplicationId: number) => packageApi.removePackage(admissionId, packageApplicationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.ip.package(admissionId) }),
  });
}

// ── Cashless discharge screen (wireframe S5) ───────────────────────────

export function useAdmissionDeposits(admissionId: number | null) {
  return useQuery({
    queryKey: keys.ip.deposits(admissionId ?? 0),
    queryFn: () => dischargeApi.fetchAdmissionDeposits(admissionId as number),
    enabled: admissionId != null,
  });
}

export function useCreateAdmissionDeposit(admissionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof dischargeApi.createAdmissionDeposit>[1]) =>
      dischargeApi.createAdmissionDeposit(admissionId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.ip.deposits(admissionId) }),
  });
}

/** The claim workspace header (`ClaimHeader.tsx`) needs age/gender/ward/bed/
 * admitted_at, none of which `GET /claims/:id` carries — rather than growing
 * that response for every caller, this reuses the existing, already-tested
 * `GET /ip/admissions/:id` the IP module itself reads from. Enabled only
 * when the claim actually has an admission (a visit-only pre-admission
 * claim, design D1, has none). */
export function useAdmissionSummary(admissionId: number | null) {
  return useQuery({
    queryKey: keys.ip.admissionSummary(admissionId ?? 0),
    queryFn: (): Promise<Admission> => getAdmission(admissionId as number),
    enabled: admissionId != null,
  });
}

export function useIpRunningBill(admissionId: number | null) {
  return useQuery({
    queryKey: keys.ip.runningBill(admissionId ?? 0),
    queryFn: () => dischargeApi.fetchIpRunningBill(admissionId as number),
    enabled: admissionId != null,
  });
}

// ── Claims desk worklist/workspace support (Phase 4.5.2/4.5.3) ─────────

/** `id -> full_name` for the worklist's Owner column/filter and the
 * workspace Timeline's actor names — `GET /api/users` needs only
 * `requireAuth` (no module grant), so this is safe for any claims:desk
 * holder to call, same as the doctor-roster pickers elsewhere already do. */
export function useUsersMap() {
  const query = useQuery({
    queryKey: keys.config.users(),
    queryFn: () => getAllUsers(),
    staleTime: 5 * 60 * 1000,
  });
  const map = new Map<number, string>();
  for (const u of query.data ?? []) map.set(u.id, u.full_name);
  return map;
}
